import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { WLLSUploadTab } from '@/components/admin/wlls/WLLSUploadTab';
import { WLLSAnalysisTab } from '@/components/admin/wlls/WLLSAnalysisTab';
import { WLLSResultsTab } from '@/components/admin/wlls/WLLSResultsTab';
import { WLLSHonestBacktestTab } from '@/components/admin/wlls/WLLSHonestBacktestTab';

export const WLLSAnalysisPage = () => {
  const [activeTab, setActiveTab] = useState<'upload' | 'analysis' | 'results' | 'honest-backtest'>('upload');
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [refreshFiles, setRefreshFiles] = useState(0);

  const handleFileUploaded = (fileId: string) => {
    setSelectedFileId(fileId);
    setActiveTab('analysis');
    setRefreshFiles(prev => prev + 1);
  };

  const handleFileSelected = (fileId: string) => {
    setSelectedFileId(fileId);
  };

  const handleFileDeleted = (fileId: string) => {
    if (selectedFileId === fileId) {
      setSelectedFileId(null);
      setActiveTab('upload');
    }
    setRefreshFiles(prev => prev + 1);
  };

  const handleAnalysisComplete = () => {
    setActiveTab('results');
    setRefreshFiles(prev => prev + 1);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">WLLS Pattern Analysis</h1>
          <p className="text-muted-foreground">
            Win/Lose Long/Short trading pattern analysis tool
          </p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-primary mt-0.5" />
            <div className="text-sm space-y-1">
              <p className="font-medium">WLLS Analysis Format:</p>
              <p className="text-muted-foreground">
                Upload CSV files with EXIT_LONG/EXIT_SHORT trades. 
                Each trade generates a token like <code className="bg-background px-1 rounded">1L</code> (win long), 
                <code className="bg-background px-1 rounded">0S</code> (loss short), etc.
              </p>
              <p className="text-muted-foreground">
                Required columns: Type, P&L USD (positive = win, negative = loss)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="upload">Upload</TabsTrigger>
          <TabsTrigger value="analysis" disabled={!selectedFileId}>Analysis</TabsTrigger>
          <TabsTrigger value="results" disabled={!selectedFileId}>Results</TabsTrigger>
          <TabsTrigger value="honest-backtest" disabled={!selectedFileId}>Honest Backtest</TabsTrigger>
        </TabsList>
        
        <TabsContent value="upload">
          <WLLSUploadTab
            onFileUploaded={handleFileUploaded}
            onFileSelected={handleFileSelected}
            onFileDeleted={handleFileDeleted}
            refresh={refreshFiles}
          />
        </TabsContent>
        
        <TabsContent value="analysis">
          {selectedFileId && (
            <WLLSAnalysisTab
              fileId={selectedFileId}
              onAnalysisComplete={handleAnalysisComplete}
            />
          )}
        </TabsContent>
        
        <TabsContent value="results">
          {selectedFileId && (
            <WLLSResultsTab fileId={selectedFileId} />
          )}
        </TabsContent>
        
        <TabsContent value="honest-backtest">
          {selectedFileId && (
            <WLLSHonestBacktestTab fileId={selectedFileId} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};